import { Button, Dropdown, Switch } from "antd";
import { capitalize } from "lodash";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import CopyIcon from "../../../../../assets/icons/CopyIcon";
import DeleteIcon from "../../../../../assets/icons/DeleteIcon";
import EditIcon from "../../../../../assets/icons/EditIcon";
import ThreeDotsIcon from "../../../../../assets/icons/ThreeDotsIcon";
import ActionModal from "../../../../../components/ui/ActionModal";
import {
  useDeleteTemplateMutation,
  useUpdateTemplateStatusMutation,
} from "../../../../../features/superAdmin/ticketService/template/template.api";
import useCopy from "../../../../../hooks/useCopy";
import useError from "../../../../../hooks/useError";
import useModal from "../../../../../hooks/useModal";
import { toast } from "../../../../../lib/toast";
import { TEMPLATE_TYPES } from "../../constants/ticket.constants";

export default function TemplateCard({ template }: { template: any }) {
  const handleCopy = useCopy();
  const { open, handleClose, handleOpen } = useModal();
  const [
    updateStatus,
    { isError: isUpdateError, error: updateError, isSuccess: isUpdated },
  ] = useUpdateTemplateStatusMutation();
  const [
    deleteTemplate,
    {
      isLoading: deleting,
      isSuccess: deleted,
      isError: isDeleteError,
      error: deleteError,
    },
  ] = useDeleteTemplateMutation();

  useError(isUpdateError, updateError, "Template status update fail");
  useError(isDeleteError, deleteError, "Template delete fail");

  const handleUpdateTemplateStatus = (newStatus: any) => {
    const id = template?.id;
    const data = {
      status: newStatus.toLowerCase(),
    };
    updateStatus({ id, data });
  };
  const handleDeleteTemplate = () => {
    deleteTemplate({ id: template?.id });
  };

  useEffect(() => {
    if (isUpdated) {
      toast("success", "Template status updated successfully!");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdated]);

  useEffect(() => {
    if (deleted) {
      toast("success", "Template deleted successfully!");
    }
    if (!deleting) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted, deleting]);
  return (
    <div className="bg-white p-4 border border-[#D7DFE9] rounded-md space-y-2">
      {open.type === "VIEW" && (
        <ActionModal
          open={open?.open}
          loading={deleting}
          title={"Do you want to delete this template?"}
          subTitle={
            "Are you sure you want to delete this template? This action cannot be."
          }
          iconType="danger"
          actionBtnText={`Yes, Delete`}
          actionBtnIcon
          actionBtnIconType="delete"
          actionBtnType="danger-light"
          cancelButton={false}
          handleClose={handleClose}
          handleAction={handleDeleteTemplate}
          handleCancel={handleClose}
        />
      )}
      <div className="flex justify-between">
        <div className="text-[#455468] text-[20px]">{template?.name}</div>
        <div>
          <Dropdown
            placement="bottomRight"
            menu={{
              items: [
                {
                  label: (
                    <div className="flex justify-between items-center">
                      <span>Active</span>
                      <span>
                        <Switch
                          checked={template?.status === "ACTIVE"}
                          onChange={() =>
                            handleUpdateTemplateStatus(
                              template?.status === "ACTIVE"
                                ? "INACTIVE"
                                : "ACTIVE",
                            )
                          }
                        />
                      </span>
                    </div>
                  ),
                  key: "0",
                },
                {
                  type: "divider",
                },
                {
                  label: (
                    <Link
                      to={`/admin/ticket-management/all-templates/edit-ticket/${template.id}`}
                    >
                      <div className="flex gap-2 items-center">
                        <span>
                          <EditIcon />
                        </span>
                        <span>Edit Template</span>
                      </div>
                    </Link>
                  ),
                  key: "2",
                },
                {
                  type: "divider",
                },
                {
                  label: (
                    <div
                      onClick={() => handleOpen({ type: "VIEW" })}
                      className="flex gap-2 items-center"
                    >
                      <span>
                        <DeleteIcon />
                      </span>
                      <span>Delete </span>
                    </div>
                  ),
                  key: "3",
                },
              ],
            }}
            trigger={["click"]}
          >
            <div className="cursor-pointer">
              <ThreeDotsIcon />
            </div>
          </Dropdown>
        </div>
      </div>
      <div className="flex gap-2 items-center">
        <span
          className={`status ${
            template?.type === TEMPLATE_TYPES.sla
              ? "status-primary"
              : "status-info"
          }`}
        >
          {template?.type === TEMPLATE_TYPES.sla
            ? "SLA Based"
            : "Workflow Based"}
        </span>
        <div className="flex items-center gap-2">
          <span>{template?.template_key}</span>
          <span
            className="cursor-pointer"
            onClick={() => handleCopy(template?.template_key as string)}
          >
            <CopyIcon />
          </span>
        </div>
      </div>

      <div className="my-4 flex gap-2">
        <div
          className={
            template?.status === "ACTIVE"
              ? "status status-success"
              : "status-danger"
          }
        >
          {capitalize(template?.status)}
        </div>
      </div>
      <Link
        className="block !mt-5"
        to={`/admin/ticket-management/all-templates/template-details/${template.id}`}
      >
        <Button className="button-bordered-light" block>
          View Template
        </Button>
      </Link>
    </div>
  );
}
